import { useState } from 'react';

import {
  BpkButtonV2,
  BUTTON_TYPES,
} from '@skyscanner/backpack-web/bpk-component-button';
import BpkDialog from '@skyscanner/backpack-web/bpk-component-dialog';
import BpkMobileScrollContainer from '@skyscanner/backpack-web/bpk-component-mobile-scroll-container';
import BpkSectionHeader from '@skyscanner/backpack-web/bpk-component-section-header';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import AmenityGroups from './AmenityGroups';
import AmenityHeader from './AmenityHeader';
import MainAmenities from './MainAmenities';

import type { HotelAmenityCategories } from '@shared/types/hotel-content';
import type { I18nService } from '@shared/types/i18n';

import STYLES from './AccommodationAmenities.module.scss';

import { filterAndProcessAmenities } from '@hotels-hotel-detail/utils/amenities';
import BpkBreakpoint, { BREAKPOINTS } from '@shared/ui/common/BpkBreakpoint';
import { withI18n } from '@shared/ui/contexts/i18n';
import { getApplicationElement } from '@shared/utils-frontend/ApplicationElements';

const cls = cssModules(STYLES);

type Props = {
  amenityCategories: HotelAmenityCategories;
  collapsed: boolean;
  i18n: I18nService;
};

const AccommodationAmenities = (props: Props) => {
  const { amenityCategories, collapsed, i18n } = props;
  const [amenitiesDialogOpen, setAmenitiesDialogOpen] = useState(false);

  const { groups, mainAmenities, topMainAmenities } =
    filterAndProcessAmenities(amenityCategories);

  if (topMainAmenities.length === 0) {
    return null;
  }

  return (
    <BpkBreakpoint query={BREAKPOINTS.MOBILE}>
      {(isActive: boolean) =>
        isActive ? (
          <>
            <AmenityHeader groups={groups} seeMoreLess />
            <BpkMobileScrollContainer>
              <MainAmenities mainAmenities={mainAmenities} />
            </BpkMobileScrollContainer>
          </>
        ) : (
          <>
            <AmenityHeader groups={groups} />
            <MainAmenities mainAmenities={topMainAmenities} />
            {collapsed ? (
              <>
                <div className={cls('AccommodationAmenities__viewAll')}>
                  <BpkButtonV2
                    type={BUTTON_TYPES.secondary}
                    onClick={() => setAmenitiesDialogOpen(true)}
                    data-testid="view-more-amenities-btn"
                  >
                    {i18n.translate('Amenities_HDP_view_all_amenities')}
                  </BpkButtonV2>
                </div>
                <BpkDialog
                  id="amenities-dialog"
                  className={cls('AccommodationAmenities__dialog')}
                  ariaLabel={i18n.translate('Filters_label_amenities')}
                  closeLabel={i18n.translate('Modal_close')}
                  isOpen={amenitiesDialogOpen}
                  onClose={() => setAmenitiesDialogOpen(false)}
                  getApplicationElement={getApplicationElement}
                >
                  <div className={cls('AccommodationAmenities__header')}>
                    <div className={cls('AccommodationAmenities__section')}>
                      <BpkSectionHeader
                        title={i18n.translate('Filters_label_amenities')}
                      />
                    </div>
                  </div>
                  <AmenityGroups groups={groups} dialog />
                </BpkDialog>
              </>
            ) : (
              <AmenityGroups groups={groups} />
            )}
          </>
        )
      }
    </BpkBreakpoint>
  );
};

export default withI18n(AccommodationAmenities);
